import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { DialogActions, DialogContentText, FormControlLabel, Switch, Box, Typography, Checkbox } from "@mui/material";
import { SSOLoginButton } from "../component/sso/SSOLoginButton";
import { configStore } from "../../public/config";
import { PUBLIC_USERNAME, PUBLIC_PASSWORD } from "../constants";

export default function NeoConnectionModal({
  open,
  standalone,
  ssoSettings,
  connection,
  dismissable = false,
  createConnection,
  onConnectionModalClose,
  onSSOAttempt,
}) {
  const [ssoVisible, setSsoVisible] = React.useState(ssoSettings["ssoEnabled"]);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [database, setDatabase] = React.useState(
    new URLSearchParams(window.location.search).get("db") || ""
  );
  const [rememberMe, setRememberMe] = React.useState(false);

  useEffect(() => {
    setSsoVisible(ssoSettings["ssoEnabled"]);
  }, [JSON.stringify(ssoSettings)]);

  const discoveryAPIUrl = ssoSettings && ssoSettings.ssoDiscoveryUrl;

  return (
    <div>
      <Dialog
        maxWidth="lg"
        open={open}
        onClose={() => {
          dismissable ? onConnectionModalClose() : null;
        }}
        aria-labelledby="form-dialog-title"
        PaperProps={{
          style: {
            borderRadius: '24px',
            overflow: 'hidden',
            background: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            maxWidth: '900px',
          }
        }}
      >
        <Box sx={{ display: 'flex', minHeight: '500px' }}>
          {/* Left side - Welcome section */}
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #d4e4b8 0%, #c7d7aa 100%)',
              position: 'relative',
              overflow: 'hidden',
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M20 50 Q30 30 40 50 T60 50\' fill=\'%23b8d89e\' opacity=\'0.3\'/%3E%3C/svg%3E")',
                opacity: 0.4,
                backgroundRepeat: 'repeat',
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                <img
                  style={{ width: "32px", height: "32px" }}
                  src="/plogo.png"
                  alt="Logo"
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3319', fontFamily: 'serif' }}>
                  PlantGenie
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#2d3319',
                  mb: 2,
                  lineHeight: 1.2,
                  fontFamily: 'serif',
                }}
              >
                Welcome Back to,
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#2d3319',
                  mb: 3,
                  lineHeight: 1.2,
                  fontFamily: 'serif',
                }}
              >
                GenieBoard!
              </Typography>

              <Typography variant="body1" sx={{ color: '#4a5a2f', mb: 1 }}>
                Not currently a PlantGenie customer?
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5a2f', mb: 4 }}>
                We'd bet you'd like to be:
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    color: '#2d3319',
                    textTransform: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.63)',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    }
                  }}
                >
                  MEET PLANTGENIE
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    color: '#2d3319',
                    borderColor: '#2d3319',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#2d3319',
                      backgroundColor: 'rgba(45, 51, 25, 0.05)',
                    }
                  }}
                >
                  MORE ABOUT US
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Right side - Login form */}
          <Box sx={{ flex: 1, p: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <DialogTitle id="form-dialog-title" sx={{ textAlign: 'left', p: 0, mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#000', fontFamily: 'serif' }}>
                Login to your admin panel
              </Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onConnectionModalClose();
                  createConnection(
                    configStore.connectionProtocol,
                    configStore.connectionHostname,
                    configStore.connectionPort,
                    database,
                    username,
                    password
                  );
                }}
              >
                {!ssoVisible ? (
                  <>
                    <TextField
                      autoFocus
                      margin="dense"
                      id="dbusername"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      label="E-mail"
                      placeholder="ronmaxwell@plantify.com"
                      type="text"
                      fullWidth
                      variant="outlined"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: '#C7D7AA',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#C7D7AA',
                          }
                        }
                      }}
                    />

                    <TextField
                      margin="dense"
                      id="dbpassword"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      label="Password"
                      type="password"
                      fullWidth
                      variant="outlined"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          '& fieldset': {
                            borderColor: 'transparent',
                          },
                          '&:hover fieldset': {
                            borderColor: '#C7D7AA',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#C7D7AA',
                          }
                        }
                      }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          sx={{
                            color: '#C7D7AA',
                            '&.Mui-checked': {
                              color: '#C7D7AA',
                            },
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ color: '#666' }}>Remember me</Typography>}
                      sx={{ mb: 3 }}
                    />
                  </>
                ) : (
                  <></>
                )}

                {ssoSettings["ssoEnabled"] ? (
                  <FormControlLabel
                    style={{ marginTop: "20px", marginBottom: "10px" }}
                    control={
                      <Switch
                        checked={ssoVisible}
                        onChange={(e) => setSsoVisible(!ssoVisible)}
                        name="checked"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#C7D7AA',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#C7D7AA',
                          },
                        }}
                      />
                    }
                    label="Use Single Sign-On"
                  ></FormControlLabel>
                ) : (
                  <></>
                )}

                {ssoVisible ? (
                  <SSOLoginButton
                    discoveryAPIUrl={discoveryAPIUrl}
                    onSSOAttempt={onSSOAttempt}
                  />
                ) : (
                  <></>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: '8px',
                    py: 1.5,
                    backgroundColor: '#C7D7AA',
                    color: '#2d3319',
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    '&:hover': {
                      backgroundColor: '#b8c799',
                    }
                  }}
                >
                  Login
                </Button>

                <Button
                  onClick={() => {
                    onConnectionModalClose();
                    createConnection(configStore.connectionProtocol, configStore.connectionHostname, configStore.connectionPort, database, PUBLIC_USERNAME, PUBLIC_PASSWORD);
                  }}
                  fullWidth
                  variant="text"
                  sx={{
                    mt: 2,
                    color: '#666',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Continue as Guest
                </Button>
              </form>
            </DialogContent>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
}